import { Component, HostListener, TemplateRef, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { SortDescriptor, State, process } from '@progress/kendo-data-query';
import { CellClickEvent, GridComponent, GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { isRowDoubleClick } from '../common/grid-grouping';
import { toLocalState } from '../common/grid-filter';
import { MatDialog } from '@angular/material/dialog';
import { FormGroup, FormBuilder, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { LoaderService } from '../loader/loader.service';
import { ToastrService } from 'ngx-toastr';
import { CouponCodeService } from './coupon-code.service';
import { MapService } from '../service/map.service';
import { ApiService } from '../service/api.service';
import { GridSettingsService } from '../service/grid-settings.service';
import { pencilIcon } from '@progress/kendo-svg-icons';

@Component({
  selector: 'app-coupon-code',
  templateUrl: './coupon-code.component.html',
  styleUrls: ['./coupon-code.component.css'],
  standalone: false
})
export class CouponCodeComponent implements OnInit, AfterViewInit {
  readonly icons = {
    edit: pencilIcon,
  };

  @ViewChild("grid", { static: true }) gridKendo: GridComponent;

  @ViewChild("couponCodeDialog") couponCodeDialog: TemplateRef<any>;
  @ViewChild("sendmailAgainDialog") sendmailAgainDialog: TemplateRef<any>;
  @ViewChild("updateStatusDialog") updateStatusDialog: TemplateRef<any>;

  public gridHeight: number;
  searchString: any = '';
  columnList: any = [];
  CouponCodeData: any;
  gridView: GridDataResult;
  kendoLoading = false;
  total: number = 0;
  public pageableWithAll: any = {
    buttonCount: 5,
    pageSizes: [5, 10, 20, 50, 100, { text: 'All', value: 'all' }]
  };
  state: State = {
    skip: 0,
    take: 10,
    // Kendo adds each column's filter to this root - filters on different columns must all match
    filter: {
      logic: "and",
      filters: [],
    },
    sort: [
      {
        dir: "asc",
        field: "",
      },
    ],
    group: []
  };
  public sort: SortDescriptor[] = [];
  addCouponCodeFrom!: FormGroup;
  isSubmit = false;
  editTypeId: any;
  selectedRecord: any;
  action: any;
  status: boolean = false;
  isMaximized = false;
  originalOverlayStyle: Partial<CSSStyleDeclaration> = {};
  originalDialogStyle: Partial<CSSStyleDeclaration> = {};
  TableName = "CouponCode";

  paymentMethods = [
    { value: 'card', label: 'Card' },
    { value: 'upi', label: 'UPI' },
  ];

  constructor(
    private readonly dialog: MatDialog,
    private readonly FormBuilder: FormBuilder,
    private readonly couponCodeService: CouponCodeService,
    private readonly loaderService: LoaderService,
    private readonly mapService: MapService,
    private readonly toaster: ToastrService,
    private readonly api: ApiService,
    private readonly gridSettings: GridSettingsService) {

  }

  ngOnInit(): void {
    this.loadCouponCodeData();
    this.setGridHeight();
    this.LoadForm();
  }

  LoadForm() {
    this.addCouponCodeFrom = this.FormBuilder.group({
      CouponCode: ['', [Validators.required, Validators.maxLength(30), Validators.pattern('^[A-Za-z0-9]+$')]],
      RzpOfferId: ['', [Validators.required, Validators.maxLength(50)]],
      CouponDiscount: ['', [Validators.required, Validators.min(1), Validators.max(100)]],
      PaymentMethod: ['card', [Validators.required]],
      StartDate: ['', [Validators.required, this.S_notPastDateValidator()]],
      EndDate: ['', [Validators.required, this.E_notPastDateValidator()]],
      MaxDiscountAmount: [null],
      MinPurchaseAmount: [null],
    });
  }

  S_notPastDateValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (!value) return null;

      const inputDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      inputDate.setHours(0, 0, 0, 0);

      // Check if date is in the past
      if (inputDate < today) {
        return { pastDate: true };
      }

      const form = this.addCouponCodeFrom;
      const startValue = form.get('StartDate')?.value;
      const endValue = form.get('EndDate')?.value;

      if (!startValue || !endValue) return null;

      const startDate = new Date(startValue);
      const endDate = new Date(endValue);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      // Ensure end date is not before start date
      if (endDate < startDate) {
        form.get('EndDate')?.setValue(startDate);
      }

      return null;
    };
  }

  E_notPastDateValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value)
        return null;

      const start = this.addCouponCodeFrom.get('StartDate')?.value;
      const end = this.addCouponCodeFrom.get('EndDate')?.value;

      if (!start || !end)
        return null;

      const startDate = new Date(start);
      const endDate = new Date(end);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      if (endDate < startDate)
        return { endBeforeStart: true };

      const inputDate = new Date(control.value);
      const today = new Date();

      today.setHours(0, 0, 0, 0);
      inputDate.setHours(0, 0, 0, 0);

      if (inputDate < today)
        return { pastDate: true };

      return null;
    };
  }

  filterData(event: any) {
    this.searchString = event;
    this.updateKendoGridData();
  }

  // The search box and the column filters both apply, whichever changed last
  updateKendoGridData() {
    this.gridView = process(this.searchedCoupons(), toLocalState(this.state));
  }

  private searchedCoupons(): any[] {
    const search = (this.searchString ?? '').toLowerCase();
    if (!search) {
      return this.CouponCodeData;
    }
    return this.CouponCodeData.filter((item: any) =>
      Object.values(item).some(val => val?.toString().toLowerCase().includes(search)));
  }

  public pageChange(event: PageChangeEvent): void {
    this.state.skip = event.skip;
    this.updateKendoGridData();
  }

  clearList() {
    this.searchString = '';
    this.updateKendoGridData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.updateKendoGridData();
  }

  public dataStateChange(state: any): void {
    this.state = state;
    this.updateKendoGridData();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.setGridHeight();
  }

  setGridHeight(): void {
    const headerHeight = 70; // Adjust according to your header and other elements
    const footerHeight = 61;
    const otherElementsHeight = 125;
    this.gridHeight = window.innerHeight - headerHeight - footerHeight - otherElementsHeight;
  }

  closeDialog() {
    this.dialog?.closeAll();
  }

  opencouponCodeDialog(data: any) {
    this.isMaximized = false;
    if (data) {
      const updatedData = { ...data };
      if (updatedData.StartDate) {
        updatedData.StartDate = this.formatDateLocal(updatedData.StartDate);
      }
      if (updatedData.EndDate) {
        updatedData.EndDate = this.formatDateLocal(updatedData.EndDate);
      }
      this.addCouponCodeFrom.patchValue(updatedData);
      this.editTypeId = data.Id;
      this.action = "Update";
    } else {
      this.action = "Add";
      this.editTypeId = 0;
      this.setDefaultFormValues();
    }

    const dialogRef = this.dialog?.open(this.couponCodeDialog, {
      width: '44vw',
      autoFocus: false,
      panelClass: 'coupon-dialog'
    });

    dialogRef?.afterClosed().subscribe(() => {
      this.setDefaultFormValues();
    });
  }
  formatDateLocal(dateString: string): string {
    const d = new Date(dateString);
    const year = d.getFullYear();
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  setDefaultFormValues() {
    this.addCouponCodeFrom.reset({
      CouponCode: '',
      RzpOfferId: '',
      CouponDiscount: '',
      PaymentMethod: 'card',
      StartDate: '',
      EndDate: '',
      MaxDiscountAmount: null,
      MinPurchaseAmount: null,
    });
  }

  submitCouponCode() {
    this.isSubmit = true;

    if (!this.addCouponCodeFrom.valid) {
      this.addCouponCodeFrom.markAllAsTouched();
      return;
    }
    const formValue = this.addCouponCodeFrom.value;

    const isDuplicate = this.CouponCodeData?.some((item: any) =>
      item.CouponCode.toLowerCase() === formValue.CouponCode.toLowerCase() &&
      item.Id !== this.editTypeId) ?? false;

    const formatLocalDate = (d: Date) => {
      const offset = d.getTimezoneOffset();
      const localDate = new Date(d.getTime() - offset * 60 * 1000);
      return localDate.toISOString().split('T')[0];
    };

    formValue.startDate = formatLocalDate(new Date(formValue.StartDate));
    formValue.endDate = formatLocalDate(new Date(formValue.EndDate));

    if (isDuplicate) {
      this.toaster.error('Duplicate coupon code is not allowed');
      return;
    }

    const payload = {
      Id: this.editTypeId,
      ...formValue
    };

    if (this.editTypeId && this.editTypeId !== 0) {
      this.updateCouponCode(payload);
    } else {
      this.addCouponCode(payload);
    }
  }

  addCouponCode(payload: any) {
    this.loaderService.show();
    this.couponCodeService.AddCouponCode(payload).subscribe((res: any) => {
      if (res["Success"]) {
        this.loaderService.hide();
        this.toaster.success('Coupon Added Successfully');
        this.closeDialog();
        this.loadCouponCodeData();
        this.isSubmit = false;
        this.editTypeId = 0;
      } else {
        this.loaderService.hide();
        this.toaster.error(res["me"]);
      }
    });
  }

  updateCouponCode(payload: any) {
    this.loaderService.show();
    this.couponCodeService.updateCouponCode(payload).subscribe({
      next: (res: any) => {
        if (res["Success"]) {
          this.loaderService.hide();
          this.toaster.success('Coupon Updated Successfully');
          this.closeDialog();
          this.loadCouponCodeData();
          this.isSubmit = false;
        } else {
          this.loaderService.hide();
          this.toaster.error('Error');
        }
      },
      error: (_err) => {
        this.loaderService.hide();
        this.toaster.error('Something went wrong Record not Updated sorry');
      }
    });
  }



  NoSend() {
    this.dialog.closeAll();
  }

  loadCouponCodeData() {
    this.loaderService.show();
    this.couponCodeService.getCouponCode().subscribe({
      next: (res: any) => {
        if (res["Success"] && res["Data"]) {
          this.CouponCodeData = res["Data"].map((item: any) => ({
            ...item
          }));
          this.columnList.push(
            "CouponCode",
            "RzpOfferId",
            "CouponDiscount",
            "StartDate",
            "EndDate",
            "CreatedOn",
            "CreatedByName",
            "IsActive"
          );
          this.loaderService.hide();
          this.updateKendoGridData();
        } else {
          this.loaderService.hide();
        }
      },
      error: (_err) => {
        this.loaderService.hide();
        this.toaster.error('Something went wrong Record not Updated sorry');
      }
    });
  }

  onCellClick(event: CellClickEvent): void {
    if (isRowDoubleClick(event) && !event.dataItem.IsExpired) {
      this.opencouponCodeDialog(event.dataItem);
    }
  }
  onlyNumbers(event: KeyboardEvent): void {
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  public onToggleStatus(dataItem: any): void {
    this.status = dataItem.IsActive;
    this.selectedRecord = dataItem;
    this.dialog.open(this.updateStatusDialog, {
      autoFocus: false,
      width: '30vw',
      disableClose: true
    })
  }

  Noupdate() {
    const index = this.CouponCodeData.findIndex((u: any) => u.Id === this.selectedRecord.Id); // or unique key
    if (index !== -1) {
      this.CouponCodeData[index].IsActive = !this.status;
    }
  }
  updateStatus(): void {
    this.dialog.closeAll();
    this.loaderService.show();
    this.couponCodeService.UpdateCouponStatus(this.selectedRecord.Id, this.status).subscribe({
      next: (res: any) => {
        if (res["Success"]) {
          this.toaster.success('Status Updated Successfully');
          this.loadCouponCodeData();
          this.closeDialog();
          this.loaderService.hide();
        } else {
          this.toaster.error('Something went wrong Record not Updated sorry');
          this.loaderService.hide();
        }
      },
      error: (_err) => {
        this.toaster.error('Something went wrong Record not Updated sorry');
        this.loaderService.hide();
      }
    });
  }

  toggleMaximize(): void {
    const overlayPane = document.querySelector('.cdk-overlay-pane:has(mat-dialog-container)');
    if (!(overlayPane instanceof HTMLElement)) return;
    const dialogElement = document.querySelector('mat-dialog-container');
    if (!(dialogElement instanceof HTMLElement)) return;
    const contentElement = dialogElement?.querySelector('.mat-dialog-content');
    if (!(contentElement instanceof HTMLElement)) return;
    this.mapService.maximizeMinimize();
    if (!overlayPane || !dialogElement) return;
    this.isMaximized = !this.isMaximized;
    if (this.isMaximized) {
      // Save original styles only once
      if (!this.originalOverlayStyle.width) {
        this.originalOverlayStyle = {
          position: overlayPane.style.position,
          top: overlayPane.style.top,
          left: overlayPane.style.left,
          margin: overlayPane.style.margin,
          width: overlayPane.style.width,
          height: overlayPane.style.height,
          maxWidth: overlayPane.style.maxWidth,
          maxHeight: overlayPane.style.maxHeight
        };

        this.originalDialogStyle = {
          width: dialogElement.style.width,
          height: dialogElement.style.height,
          maxHeight: dialogElement.style.maxHeight
        };
      }

      // Apply maximize styles
      overlayPane.style.position = 'fixed';
      overlayPane.style.top = '0';
      overlayPane.style.left = '0';
      overlayPane.style.margin = '0';
      overlayPane.style.setProperty('width', '100vw', 'important');
      overlayPane.style.setProperty('height', '100vh', 'important');
      overlayPane.style.setProperty('max-width', '100vw', 'important');
      overlayPane.style.setProperty('max-height', '100vh', 'important');

      dialogElement.style.setProperty('width', '100vw', 'important');
      dialogElement.style.setProperty('max-width', '100vw', 'important');
      contentElement.style.maxHeight = '72vh';
    } else {
      // Restore original styles
      overlayPane.style.position = this.originalOverlayStyle.position ?? '';
      overlayPane.style.top = this.originalOverlayStyle.top ?? '';
      overlayPane.style.left = this.originalOverlayStyle.left ?? '';
      overlayPane.style.margin = this.originalOverlayStyle.margin ?? '';
      overlayPane.style.width = this.originalOverlayStyle.width ?? '';
      overlayPane.style.height = this.originalOverlayStyle.height ?? '';
      overlayPane.style.maxWidth = this.originalOverlayStyle.maxWidth ?? '';
      overlayPane.style.maxHeight = this.originalOverlayStyle.maxHeight ?? '';

      dialogElement.style.width = this.originalDialogStyle.width ?? '';
      dialogElement.style.height = this.originalDialogStyle.height ?? '';
      contentElement.style.maxHeight = this.originalDialogStyle.maxHeight ?? '';
    }
  }

  onColumnVisibilityChange(columnEvent: GridComponent): void {
    this.gridSettings.saveGridSettings(columnEvent, this.TableName).subscribe();
  }

  ngAfterViewInit(): void {
    this.gridSettings.applyGridSettings(this.api, this.TableName, this.gridKendo);
  }
}
